import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BellRing, BellOff, Check } from "lucide-react";

const b64ToU8 = (s) => { const p = "=".repeat((4 - (s.length % 4)) % 4); const raw = atob((s + p).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...raw].map((c) => c.charCodeAt(0))); };
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOSBrowser = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !(window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);

export function PushConsent({ compact = false }) {
  const [state, setState] = useState("loading");

  useEffect(() => {
    (async () => {
      if (!supported()) return setState("unsupported");
      if (Notification.permission === "denied") return setState("denied");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setState(sub ? "on" : "off");
    })();
  }, []);

  const enable = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") return setState(perm === "denied" ? "denied" : "off");
      const { data } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(data.public_key) });
      await api.post("/push/subscribe", { subscription: sub.toJSON(), user_agent: navigator.userAgent });
      setState("on"); toast.success("Notifications activées");
    } catch (e) { toast.error("Impossible d’activer les notifications sur cet appareil"); }
  };

  const disable = async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); }
    setState("off"); toast.success("Notifications désactivées");
  };

  if (state === "loading") return null;
  if (state === "on") {
    return (
      <div className={`rounded-2xl border border-olive/30 bg-white p-4 flex items-center justify-between gap-3 ${compact ? "text-sm" : ""}`} data-testid="push-enabled">
        <span className="inline-flex items-center gap-2 text-olive font-medium"><Check className="w-4 h-4" /> Notifications de séjour activées sur cet appareil</span>
        <button onClick={disable} className="text-xs text-muted-foreground underline" data-testid="push-disable">Désactiver</button>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-rose-brand/30 bg-rosebrand-pale/40 p-5" data-testid="push-consent">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0"><BellRing className="w-5 h-5 text-rose-brand" /></span>
        <div className="flex-1">
          <p className="font-semibold text-olive">Recevez les infos de votre séjour</p>
          <p className="text-sm text-muted-foreground mt-1">Confirmation, rappel avant l’arrivée, code d’accès le jour J, logement prêt, infos pendant le séjour et rappel de départ — directement sur votre téléphone. Rien d’autre, et vous pouvez désactiver à tout moment.</p>
          {state === "unsupported" && <p className="text-xs text-rose-brand mt-2" data-testid="push-unsupported">{isIOSBrowser() ? "Sur iPhone, ajoutez d’abord L’Orne Park à votre écran d’accueil (bouton ci-dessus), puis ouvrez l’application pour activer les notifications." : "Votre navigateur ne prend pas en charge les notifications."}</p>}
          {state === "denied" && <p className="text-xs text-rose-brand mt-2 inline-flex items-center gap-1"><BellOff className="w-3.5 h-3.5" /> Notifications bloquées : autorisez-les dans les réglages de votre navigateur.</p>}
          {state === "off" && <Button onClick={enable} size="sm" className="mt-3 bg-rose-brand hover:bg-rosebrand-soft text-white rounded-full" data-testid="push-enable">Activer les notifications</Button>}
        </div>
      </div>
    </div>
  );
}
